import { Element, Option } from "@p0security/shared/install/types";

/** The install element that selects where an integration's secrets are
 * vaulted: an installed AWS Secrets Manager account, with the secrets'
 * region, an installed Google Secret Manager project, or an Azure Key Vault in
 * a directory with an `azure` install.
 *
 * The Azure Key Vault option is hidden until an integration's Azure port shows
 * it.
 *
 * It says nothing about where a connector runs. {@link newSecretManagerElement}
 * adds that, for integrations whose connector runs beside the vault.
 *
 * @param labels.owner Names what owns the secrets in field descriptions, e.g.
 *   "integration"
 */
export const newVaultElement = (labels: { owner: string }) =>
  Element({
    label: "Secret manager provider",
    description: `The provider where this ${labels.owner}'s secrets are stored`,
    type: "select",
    step: "new",
    default: "aws-sm",
    options: {
      "aws-sm": Option({
        maturity: "preview" as const,
        label: "AWS Secrets Manager",
        schema: {
          install: Element({
            label: "Installation",
            description: `The AWS Secrets Manager installation in P0 that stores this ${labels.owner}'s secrets`,
            type: "dynamic",
            step: "new",
          }),
          secretsRegion: Element({
            label: "Secrets region",
            description: `The AWS region in which this ${labels.owner}'s secrets are stored`,
            type: "string",
            step: "new",
          }),
        },
      }),
      "gcp-sm": Option({
        label: "Google Secret Manager",
        maturity: "preview",
        schema: {
          install: Element({
            label: "Installation",
            description: `The Google Secret Manager installation in P0 that stores this ${labels.owner}'s secrets`,
            type: "dynamic",
            step: "new",
          }),
        },
      }),
      "azure-kv": Option({
        label: "Azure Key Vault",
        maturity: "preview",
        hidden: true,
        schema: {
          directoryId: Element({
            label: "Tenant",
            description: `The Microsoft Entra tenant, installed in P0's Azure integration, whose Key Vault stores this ${labels.owner}'s secrets`,
            type: "dynamic",
            step: "new",
          }),
          vaultName: Element({
            label: "Key Vault name",
            description: `The name of the Key Vault that stores this ${labels.owner}'s secrets`,
            type: "string",
            step: "new",
          }),
        },
      }),
    },
  });

export type VaultElement = ReturnType<typeof newVaultElement>;

/** The install element that selects where P0 vaults an integration's shared
 * credential, and where that integration's connector runs: a
 * {@link newVaultElement} whose options also locate the connector, in the
 * vault's own account or project.
 *
 * @param labels.owner Names what owns the secrets in field descriptions, e.g.
 *   "GitHub App"
 * @param labels.connector Names the connector in field descriptions, e.g.
 *   "GitHub"
 */
export const newSecretManagerElement = (labels: {
  owner: string;
  connector: string;
}) => {
  const vault = newVaultElement({ owner: labels.owner });
  const {
    "aws-sm": awsSm,
    "azure-kv": azureKv,
    "gcp-sm": gcpSm,
  } = vault.options;
  return Element({
    ...vault,
    description: `The provider where P0 stores the secrets for this ${labels.owner}`,
    options: {
      "aws-sm": Option({
        ...awsSm,
        schema: {
          install: awsSm.schema.install,
          connectorRegion: Element({
            label: "Connector region",
            description: `The AWS region in which P0's ${labels.connector} connector (a Lambda function) is deployed`,
            type: "generated",
            step: "configure",
          }),
          secretsRegion: awsSm.schema.secretsRegion,
        },
      }),
      "gcp-sm": Option({
        ...gcpSm,
        schema: {
          install: gcpSm.schema.install,
          connectorServiceName: Element({
            label: "GCP Cloud Run service name",
            description: "",
            type: "generated",
          }),
          connectorServiceUri: Element({
            label: "GCP Cloud Run service URI",
            description:
              "The invocation URL of the connector's Cloud Run service, resolved once at install time.",
            type: "generated",
          }),
          connectorServiceAccount: Element({
            label: "GCP Cloud Run service account",
            description:
              "The service account that the Cloud Run connector is running as",
            type: "generated",
          }),
          connectorRegion: Element({
            label: "Region",
            description:
              "The GCP region in which the connector is provisioned.",
            type: "hidden",
            step: "new",
          }),
        },
      }),
      // Choosing Key Vault also puts the connector on Azure Container Apps,
      // in the same tenant, as Google Secret Manager puts it on Cloud Run.
      "azure-kv": Option({
        ...azureKv,
        schema: {
          directoryId: azureKv.schema.directoryId,
          subscriptionId: Element({
            label: "Subscription ID",
            description: `The Azure subscription in which P0's ${labels.connector} connector (a Container App) and its Key Vault are deployed`,
            type: "string",
            step: "new",
          }),
          connectorRegion: Element({
            label: "Region",
            description: `The Azure region in which P0's ${labels.connector} connector is deployed, e.g. eastus`,
            type: "string",
            step: "new",
          }),
          connectorServiceUri: Element({
            label: "Container App URL",
            description:
              "The URL of the connector's Container App, resolved once at install time.",
            type: "generated",
          }),
          connectorClientId: Element({
            label: "Connector app registration",
            description:
              "The client ID of the connector's app registration, which P0 requests its tokens for, resolved once at install time.",
            type: "generated",
          }),
          // Generated, unlike the vault element's, because the connector's
          // Terraform names its dedicated vault.
          vaultName: Element({
            label: "Key Vault name",
            description:
              "The name of the connector's dedicated Key Vault, resolved once at install time.",
            type: "generated",
          }),
        },
      }),
    },
  });
};

export type SecretManagerElement = ReturnType<typeof newSecretManagerElement>;

/** A {@link newVaultElement} without its Azure Key Vault option, for an
 * integration whose connector the customer hosts, which P0 can't call on
 * Azure yet.
 *
 * Hiding the option would only keep it off the form, and an install request
 * could still name it, so these integrations drop it.
 */
export const withoutAzureKeyVault = (vault: VaultElement) => {
  const { "azure-kv": _, ...options } = vault.options;
  return Element({ ...vault, options });
};
